"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

/**
 * 全局滚动重置组件 —— 唯一权威入口
 * ------------------------------------------------------------
 * 覆盖所有路由场景：
 *
 * 【场景 A】跨页导航  → usePathname 变化
 * 【场景 B】同页点击  → 在 /blog 页面点 href="/blog"（pathname 不变）
 * 【场景 C】query 变化 → /blog?tab=a → /blog?tab=b（click 代理覆盖）
 * 【场景 D】浏览器前进/后退
 *
 * 实现方式：两层加固
 *   ① pathname effect    → 覆盖 A / D（跨页 + 前进后退）
 *   ② 全局 click 代理    → 覆盖 B / C（同页点击 & query 变化）
 *      - capture phase 监听所有 <a> 点击
 *      - 忽略 hash 锚点（href="#xxx" 时用户要跳到 section，不强制滚顶）
 *      - 忽略外部链接 / target=_blank / mailto / tel 等
 *
 * 所有滚动一律 behavior: "auto"，**绝不平滑动画**，
 * 彻底消除"跳转后从下方滚上来"的观感。
 */
export default function ScrollReset() {
    const pathname = usePathname();

    // ① 跨页 / 浏览器前进后退 → 立即置顶
    useEffect(() => {
        doScrollTop();
    }, [pathname]);

    // ② 同页点击入口（含 query 变化）→ 也强制置顶
    useEffect(() => {
        const handleClick = (e) => {
            const anchor = e.target.closest("a");
            if (!anchor) return;
            const href = anchor.getAttribute("href");
            if (!href) return;

            // 跳过：新窗口打开 / 下载 / 非 http 协议
            if (anchor.target === "_blank" || anchor.hasAttribute("download")) return;
            if (href.startsWith("http:") || href.startsWith("https:") || href.startsWith("//")) return;
            if (href.startsWith("mailto:") || href.startsWith("tel:")) return;

            let targetUrl;
            try {
                targetUrl = new URL(href, window.location.origin);
            } catch {
                return;
            }

            // 有 hash 锚点 → 用户要跳到 section，不强制滚顶
            if (targetUrl.hash) return;

            // pathname 也不同 → 上面的 pathname effect 会处理，这里让 Next.js 先处理
            if (targetUrl.pathname !== window.location.pathname) return;

            // === 同页（含 query 变化）===
            // Next.js Link 同页点击不会触发 usePathname 变化，
            // 我们在 capture phase 捕获后，让 Next.js 正常完成导航，下一帧立即置顶
            requestAnimationFrame(() => doScrollTop());
        };

        // capture phase 先于 Next.js Link 的 onClick 执行
        document.addEventListener("click", handleClick, true);
        return () => document.removeEventListener("click", handleClick, true);
    }, []);

    return null;
}

/** 立即置顶，不做任何平滑动画 */
function doScrollTop() {
    // 双重保险：同时调用 window 和 DOM API
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
}
